import { Action, Reducer } from 'redux';

// actions
import * as Actions from '../actions';
import * as ActionEvents from '../actions/Staff.Actions';

// models
import { Staff } from '../../models/Entities/Staff/Staff';

// state
import { StaffState, StaffUnloadState } from '../states/StaffState';

export type KnownAction =
  | Actions.Staff.StaffGetAllRequestAction
  | Actions.Staff.StaffGetAllSuccessAction
  | Actions.Staff.StaffGetAllFailureAction
  | Actions.Staff.StaffGetByIdRequestAction
  | Actions.Staff.StaffGetByIdSuccessAction
  | Actions.Staff.StaffGetByIdFailureAction
  | Actions.Staff.StaffCreateRequestAction
  | Actions.Staff.StaffCreateSuccessAction
  | Actions.Staff.StaffCreateFailureAction
  | Actions.Staff.StaffUpdateRequestAction
  | Actions.Staff.StaffUpdateSuccessAction
  | Actions.Staff.StaffUpdateFailureAction
  | Actions.Staff.StaffDeleteRequestAction
  | Actions.Staff.StaffDeleteSuccessAction
  | Actions.Staff.StaffDeleteFailureAction;

export const StaffReducer: Reducer<StaffState> = (
  state: StaffState | undefined,
  incomingAction: Action
): StaffState => {
  if (state === undefined) {
    return StaffUnloadState as StaffState;
  }

  const action = incomingAction as KnownAction;

  switch (action.type) {
    case ActionEvents.STAFF_GETALL_REQUEST:
      return {
        ...state,
        isLoadingAll: true,
        successLoadingAll: undefined,
        failOnLoadingAll: false,
        // El filtro se guarda al pedir y no al responder: es lo que hay que
        // repetir si la operación que sigue obliga a recargar la página.
        filter: action.filter,
        error: undefined
      };

    case ActionEvents.STAFF_GETALL_SUCCESS:
      return {
        ...state,
        isLoadingAll: false,
        successLoadingAll: true,
        failOnLoadingAll: false,
        // Se reemplaza, no se mezcla: esto es una página del servidor, y acumular
        // páginas de búsquedas distintas mostraría filas que no corresponden al
        // filtro puesto.
        list: action.payload,
        pagination: action.pagination,
        error: undefined
      };

    case ActionEvents.STAFF_GETALL_FAILURE:
      return {
        ...state,
        isLoadingAll: false,
        successLoadingAll: false,
        failOnLoadingAll: true,
        error: action.error
      };

    case ActionEvents.STAFF_GETBYID_REQUEST:
      return {
        ...state,
        isLoadingOne: true,
        successLoadingOne: undefined,
        failOnLoadingOne: false,
        error: undefined
      };

    case ActionEvents.STAFF_GETBYID_SUCCESS: {
      const others = state.list.filter((staff: Staff) => staff.id !== action.payload.id);
      return {
        ...state,
        isLoadingOne: false,
        successLoadingOne: true,
        failOnLoadingOne: false,
        list: [...others, action.payload],
        error: undefined
      };
    }

    case ActionEvents.STAFF_GETBYID_FAILURE:
      return {
        ...state,
        isLoadingOne: false,
        successLoadingOne: false,
        failOnLoadingOne: true,
        error: action.error
      };

    case ActionEvents.STAFF_CREATE_REQUEST:
      return {
        ...state,
        isAddingNewOne: true,
        successAddingNewOne: undefined,
        failOnAddingNewOne: false,
        error: undefined
      };

    case ActionEvents.STAFF_CREATE_SUCCESS:
      return {
        ...state,
        isAddingNewOne: false,
        successAddingNewOne: true,
        failOnAddingNewOne: false,
        // La fila la trae la recarga que dispara el middleware: el alta responde
        // un id y con eso no se puede armar el registro.
        lastCreatedId: action.payload,
        error: undefined
      };

    case ActionEvents.STAFF_CREATE_FAILURE:
      return {
        ...state,
        isAddingNewOne: false,
        successAddingNewOne: false,
        failOnAddingNewOne: true,
        error: action.error
      };

    case ActionEvents.STAFF_UPDATE_REQUEST:
      return {
        ...state,
        isUpdatingOne: true,
        successUpdatingOne: undefined,
        failOnUpdatingOne: false,
        error: undefined
      };

    case ActionEvents.STAFF_UPDATE_SUCCESS:
      return {
        ...state,
        isUpdatingOne: false,
        successUpdatingOne: true,
        failOnUpdatingOne: false,
        error: undefined
      };

    case ActionEvents.STAFF_UPDATE_FAILURE:
      return {
        ...state,
        isUpdatingOne: false,
        successUpdatingOne: false,
        failOnUpdatingOne: true,
        error: action.error
      };

    case ActionEvents.STAFF_DELETE_REQUEST:
      return {
        ...state,
        isDeletingOne: true,
        successDeletingOne: undefined,
        failOnDeletingOne: false,
        error: undefined
      };

    case ActionEvents.STAFF_DELETE_SUCCESS:
      return {
        ...state,
        isDeletingOne: false,
        successDeletingOne: true,
        failOnDeletingOne: false,
        // Se saca en el acto para que la fila no siga ahí mientras vuelve la
        // recarga; el total de registros lo corrige esa respuesta.
        list: state.list.filter((staff: Staff) => staff.id !== action.payload),
        error: undefined
      };

    case ActionEvents.STAFF_DELETE_FAILURE:
      return {
        ...state,
        isDeletingOne: false,
        successDeletingOne: false,
        failOnDeletingOne: true,
        error: action.error
      };

    default:
      return state;
  }
};
